import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  FileText,
  FolderOpen,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import RetreatApi from "../api/retreatApi";
import DeleteModel from "./DeleteModel";
import "../assets/css/rosterActionModals.css";

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploadModal = ({
  show,
  handleClose,
  rowId,
  handleUploadSuccess,
  fileType,
  existingFileUrl,
  onDeleteFile,
  modalTitle,
  setSelectedRetreat = null,
  setIncommingRetreats = null,
  userData,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const retreatApi = new RetreatApi();
  const isProgram = fileType === "programFile";

  useEffect(() => {
    setFileUrl(existingFileUrl);
  }, [existingFileUrl]);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setDragOver(false);
    }
  }, [show]);

  const acceptFile = (nextFile, inputEl) => {
    if (!nextFile) return;
    if (nextFile.size > MAX_FILE_BYTES) {
      toast.error("File size should be less than 50 MB");
      if (inputEl) inputEl.value = "";
      setFile(null);
      return;
    }
    setFile(nextFile);
  };

  const handleFileChange = (event) => {
    acceptFile(event.target.files[0], event.target);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const nextFile = event.dataTransfer.files?.[0];
    if (!nextFile) return;
    if (inputRef.current && nextFile.size <= MAX_FILE_BYTES) {
      try {
        const transfer = new DataTransfer();
        transfer.items.add(nextFile);
        inputRef.current.files = transfer.files;
      } catch (error) {
        console.error("Unable to attach the dropped file to the input", error);
      }
    }
    acceptFile(nextFile, inputRef.current);
  };

  const requestClose = () => {
    setFile(null);
    handleClose();
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      toast.warning("Please select a file to upload.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append(fileType, file);
    formData.append("retreatId", rowId);
    formData.append("userData", JSON.stringify(userData));
    try {
      const response = await retreatApi.uploadFiles(formData);

      if (response.success) {
        toast.success("File uploaded successfully!");
        setFileUrl(response.fileUrl);
        setFile(null);
        handleClose();
        handleUploadSuccess();
        if (setSelectedRetreat) {
          setSelectedRetreat(response?.data?.id);
          setIncommingRetreats((prevRetreats) =>
            prevRetreats.map((item) =>
              item.id === response.data.id
                ? { ...item, ...response.data }
                : item,
            ),
          );
        }
      } else {
        toast.error("File upload failed.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Error uploading file.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteFile = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteFile = async () => {
    setShowDeleteModal(false);
    try {
      const response = await retreatApi.deleteFile({
        retreatId: rowId,
        fileType,
        userData,
      });

      if (response.success) {
        toast.success("File deleted successfully!");
        setFileUrl(null);
        onDeleteFile();
      } else {
        toast.error("Failed to delete the file.");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Error deleting file.");
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={requestClose}
        centered
        className="ram-modal"
        dialogClassName="ram-dialog"
        contentClassName="ram-content"
      >
        <div className="ram-shell">
          <header className="ram-header">
            <div className="ram-mark" aria-hidden="true">
              {isProgram ? <CalendarDays size={20} /> : <FolderOpen size={20} />}
            </div>
            <div className="ram-header-copy">
              <p className="ram-kicker">Documents</p>
              <h2>{isProgram ? "Upload program" : "Upload directory"}</h2>
              <p>
                {modalTitle
                  ? modalTitle
                  : "Add the file couples will be able to open."}
              </p>
            </div>
            <button
              type="button"
              className="ram-close"
              onClick={requestClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </header>

          <form id="ram-upload-form" onSubmit={handleUploadSubmit}>
            <div className="ram-body">
              {fileUrl ? (
                <div className="ram-file-card">
                  <div>
                    <span className="ram-status">
                      <CheckCircle2 size={14} />
                      On file
                    </span>
                    <h3>
                      {isProgram ? "Program file" : "Directory file"} is ready
                    </h3>
                    <p>Open it in a new tab, or remove it and upload another.</p>
                  </div>
                  <div className="ram-file-actions">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ram-btn ram-btn-quiet"
                    >
                      <ExternalLink size={15} />
                      View file
                    </a>
                    <button
                      type="button"
                      className="ram-btn ram-btn-danger"
                      onClick={confirmDeleteFile}
                      title="Delete the uploaded file"
                    >
                      <Trash2 size={15} />
                      Delete file
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="ram-note">
                    Choose a file up to 50 MB. Couples can open it after the
                    upload finishes.
                  </p>
                  <div
                    className={`ram-drop${dragOver ? " is-over" : ""}`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={(event) => {
                      if (event.target !== inputRef.current) {
                        inputRef.current?.click();
                      }
                    }}
                  >
                    <Upload size={22} />
                    <strong>Drop a file here, or browse</strong>
                    <p>Maximum size 50 MB</p>
                    <input
                      ref={inputRef}
                      type="file"
                      onChange={handleFileChange}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </div>
                  {file ? (
                    <div className="ram-picked">
                      <FileText size={18} />
                      <div>
                        <strong>{file.name}</strong>
                        <span>{formatSize(file.size)}</span>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </form>

          <footer className="ram-footer">
            <button
              type="button"
              className="ram-btn ram-btn-quiet"
              onClick={requestClose}
              disabled={loading}
            >
              Close
            </button>
            {!fileUrl ? (
              <button
                type="submit"
                form="ram-upload-form"
                className={`ram-btn ram-btn-primary${loading ? " is-busy" : ""}`}
                disabled={loading || !file}
              >
                {loading ? (
                  <>
                    <span className="ram-spinner" />
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </button>
            ) : null}
          </footer>
        </div>
      </Modal>

      <DeleteModel
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        DeleteData={handleDeleteFile}
      />
    </>
  );
};

export default FileUploadModal;
